import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context';
import { canAccess, canSeeHolidays } from '../../utils/rbac';
import api from '../../hooks/useApi';
import { leaveAwaitingDecision, overtimeAwaitingDecision } from '../../utils/navCounts';
import HolidaysPage from './HolidaysPage';
import OvertimePage from './OvertimePage';
import { VacancyApprovals, OfferApprovals } from './HiringApprovals';
import FeatureTour from '../shared/FeatureTour';
import useFeatureTour from '../../hooks/useFeatureTour';
import { APPROVALS_TOUR_ID, approvalsTourSteps } from './approvalsTour';

// ── Everything waiting on you to decide ──────────────────────────────────────
//
// A manager had two queues in two places: Approval Requests for overtime,
// TOIL, standby and allowances, and Holiday & Absence Requests for leave.
// Underneath they share nothing — different tables, different allowance rules,
// different gates — and on the surface they are the same job, so somebody
// looking for what needed deciding had to know which screen held which kind.
// Approval Requests had also become a catch-all, having grown Vacancies and
// Offers tabs that have nothing to do with overtime.
//
// One page, a tab per kind (Matt, 21 September 2026). The tabs are the
// existing screens, so the specialist tooling stays where it belongs: pro-rata
// and allowance edits with leave, rates and the payroll report with overtime.
// A single undifferentiated queue was the other candidate and reads closest to
// the job, but My Actions already exists to carry decisions and a second one
// would compete with it.
//
// WHO SEES WHAT. Each tab asks its own question, the same one the screen asked
// when it stood alone: leave is the line-manager test, overtime is the page
// grant an administrator sets in Roles & Permissions, and hiring is the
// hiring:approve-vacancy and hiring:approve-offer grants, set in the same place. The shell never widens any of
// them — somebody with no tabs is told so rather than shown an empty page, and
// the menu entry is not offered to them in the first place.
export default function ApprovalsPage({ tab: requestedTab }) {
  const { currentUser } = useApp();

  const maySeeLeave = canSeeHolidays(currentUser);
  const maySeeOvertime = canAccess('overtime');

  // Hiring is two grants, one for vacancies and one for above-band offers. The
  // endpoints answer holders with what is waiting and everyone else with an
  // empty list, so the tab shows when there is something for this person to
  // decide.
  const [vacancies, setVacancies] = useState([]);
  const [offers, setOffers] = useState([]);
  const loadVacancies = useCallback(
    () => api.get('talent/approvals').then(d => setVacancies(Array.isArray(d) ? d : [])).catch(() => setVacancies([])),
    [],
  );
  const loadOffers = useCallback(
    () => api.get('talent/offer-approvals').then(d => setOffers(Array.isArray(d) ? d : [])).catch(() => setOffers([])),
    [],
  );
  useEffect(() => { loadVacancies(); loadOffers(); }, [loadVacancies, loadOffers]);

  // What is waiting, for the count on each tab. The leave figure is the one
  // this person may actually decide, which the server answers per row, so the
  // badge cannot promise work that turns out to be somebody else's.
  const [leavePending, setLeavePending] = useState(0);
  useEffect(() => {
    if (!maySeeLeave) return;
    api.get('leave-requests')
      .then(rows => setLeavePending(leaveAwaitingDecision(rows)))
      .catch(() => {});
  }, [maySeeLeave]);

  const [overtimePending, setOvertimePending] = useState(0);
  useEffect(() => {
    if (!maySeeOvertime) return;
    api.get('overtime-requests?status=pending')
      .then(rows => setOvertimePending(overtimeAwaitingDecision(rows, currentUser?.id)))
      .catch(() => {});
  }, [maySeeOvertime, currentUser?.id]);

  const hiringCount = vacancies.length + offers.length;
  const tabs = [
    ...(maySeeLeave ? [{ id: 'leave', label: 'Leave', count: leavePending }] : []),
    ...(maySeeOvertime ? [{ id: 'overtime', label: 'Overtime & allowances', count: overtimePending }] : []),
    ...(hiringCount ? [{ id: 'hiring', label: 'Hiring', count: hiringCount }] : []),
  ];

  // The address decides the tab where it names one, so #approvals/leave and the
  // old #holidays land where the person meant to be. Otherwise the first tab
  // they hold, which for most people is the only one.
  const [tab, setTab] = useState(requestedTab || null);
  useEffect(() => { if (requestedTab) setTab(requestedTab); }, [requestedTab]);
  const active = tabs.some(t => t.id === tab) ? tab : tabs[0]?.id;

  // This page's own tour, for everybody who opens it. That two screens were
  // merged to make it is not this tour's job to say: somebody hunting for a
  // page that has moved needs telling wherever they land, which is what the
  // app-wide What's changed notice does (utils/changeLog).
  // Held back until a tab exists, so the tour does not open onto a page still
  // working out whether this person has anything to decide, with nothing yet
  // for a spotlight to land on.
  const tour = useFeatureTour(APPROVALS_TOUR_ID, { enabled: tabs.length > 0 });

  if (!tabs.length) {
    return (
      <div className="fade-in">
        <h2 className="text-base font-extrabold text-navy mb-1">Approvals</h2>
        <div className="bg-white border border-gray-200 rounded p-6 text-center text-[11px] text-gray-500">
          Nothing is waiting on you. Approvals appear here when somebody in your team asks for leave,
          claims overtime, or you are named to decide a vacancy or an offer.
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <div data-tour="approvals-header" className="mb-3">
        <h2 className="text-base font-extrabold text-navy flex items-center gap-1.5">
          Approvals
          <button onClick={tour.start} aria-label="Replay the guided tour of this page" title="Replay the guided tour of this page"
            className="w-4 h-4 rounded-full border border-gray-200 bg-white text-gray-500 text-[9px] font-bold leading-none hover:border-gold hover:text-navy">?</button>
        </h2>
        <div className="text-[9px] text-gray-500">Everything waiting on you to decide, for your team</div>
      </div>

      <div data-tour="approval-tabs" className="flex gap-1 mb-3 flex-wrap">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-1 text-[10px] font-semibold rounded ${active === t.id ? 'bg-navy text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            {t.label}
            {t.count > 0 && (
              <span className="ml-1 bg-amber-400 text-amber-950 text-[8px] font-bold px-1 rounded-full">{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {active === 'leave' && <HolidaysPage embedded />}
      {active === 'overtime' && <OvertimePage embedded />}
      {active === 'hiring' && (
        <div className="space-y-4">
          {vacancies.length > 0 && <VacancyApprovals vacancies={vacancies} onDecided={loadVacancies} />}
          {offers.length > 0 && <OfferApprovals offers={offers} onDecided={loadOffers} />}
        </div>
      )}
      {/* The tour's conditions are these same facts, so a tab somebody does
          not have costs them no step. Hiring is left out of it: it comes and
          goes with what is actually sitting with them. */}
      {tour.open && (
        <FeatureTour steps={approvalsTourSteps} context={{ maySeeLeave, maySeeOvertime }}
          onFinish={tour.finish} onSkip={tour.skip} />
      )}
    </div>
  );
}
